import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Row, Col, Statistic, Table, Select, Spin, Typography, Tag, Empty,
  Input, Drawer, Tabs, Space, Tooltip, Button, notification,
} from 'antd';
import {
  TeamOutlined, UserOutlined, MessageOutlined, DollarOutlined,
  DashboardOutlined, ApiOutlined, ReloadOutlined, RiseOutlined,
  ClockCircleOutlined, EyeOutlined, TableOutlined, FireOutlined,
} from '@ant-design/icons';
import {
  getUserActivitySummary, getUserActivityDetailed, getUserActivityDetail,
} from '../services/adminService';

const { Title, Text } = Typography;

const MODEL_LABELS = {
  'claude-haiku-4-5': 'Haiku 4.5',
  'claude-sonnet-4-6': 'Sonnet 4.6',
  'claude-opus-4-6': 'Opus 4.6',
  unknown: 'Legacy',
};

const ROLE_COLORS = { Admin: 'red', Creator: 'blue', User: 'default' };

function fmtDate(iso) {
  if (!iso) return <Text type="secondary">—</Text>;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtRelative(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function UserActivity() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        getUserActivitySummary(days),
        getUserActivityDetailed(days),
      ]);
      setSummary(s);
      setUsers(u.users || []);
    } catch (err) {
      console.error('Failed to load user activity:', err);
      notification.error({
        message: 'Could not load user activity',
        description: err?.message || 'Unknown error — see console for details.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [days]); // eslint-disable-line

  const openUser = async (userId) => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDetail(null);
    try {
      const d = await getUserActivityDetail(userId, days);
      setDetail(d);
    } catch (err) {
      console.error('Failed to load user detail:', err);
      notification.error({
        message: 'Could not load user details',
        description: err?.message || 'Unknown error — see console for details.',
      });
      setDrawerOpen(false);
    } finally {
      setDrawerLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.full_name || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const columns = [
    {
      title: 'User',
      key: 'user',
      fixed: 'left',
      width: 240,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.full_name || r.username}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.email || '—'}</Text>
        </Space>
      ),
      sorter: (a, b) => (a.username || '').localeCompare(b.username || ''),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (v, r) => (
        <Space size={4}>
          <Tag color={ROLE_COLORS[v] || 'default'}>{v}</Tag>
          {r.is_deleted && <Tag color="default">deleted</Tag>}
          {!r.is_active && !r.is_deleted && <Tag color="orange">disabled</Tag>}
          {r.approval_status === 'pending' && <Tag color="gold">pending</Tag>}
        </Space>
      ),
      filters: [
        { text: 'Admin', value: 'Admin' },
        { text: 'Creator', value: 'Creator' },
        { text: 'User', value: 'User' },
      ],
      onFilter: (val, r) => r.role === val,
    },
    {
      title: <Tooltip title="AI chat messages sent"><span><MessageOutlined /> Messages</span></Tooltip>,
      key: 'messages',
      width: 110,
      align: 'right',
      render: (_, r) => r.ai.messages.toLocaleString(),
      sorter: (a, b) => a.ai.messages - b.ai.messages,
      defaultSortOrder: 'descend',
    },
    {
      title: <Tooltip title="Input + output tokens"><span>Tokens</span></Tooltip>,
      key: 'tokens',
      width: 120,
      align: 'right',
      render: (_, r) => r.ai.total_tokens.toLocaleString(),
      sorter: (a, b) => a.ai.total_tokens - b.ai.total_tokens,
    },
    {
      title: <span><DollarOutlined /> Cost</span>,
      key: 'cost',
      width: 100,
      align: 'right',
      render: (_, r) => `$${r.ai.cost.toFixed(2)}`,
      sorter: (a, b) => a.ai.cost - b.ai.cost,
    },
    {
      title: <span><DashboardOutlined /> Dashboards</span>,
      key: 'dashboards',
      width: 150,
      align: 'right',
      render: (_, r) => (
        <Tooltip title={`${r.dashboards.distinct_dashboards} distinct dashboards`}>
          <span>{r.dashboards.views.toLocaleString()} views</span>
        </Tooltip>
      ),
      sorter: (a, b) => a.dashboards.views - b.dashboards.views,
    },
    {
      title: <span><ApiOutlined /> Graph API</span>,
      key: 'graphql',
      width: 150,
      align: 'right',
      render: (_, r) => (
        <Tooltip title={`${r.graphql.distinct_tables} distinct tables`}>
          <span>{r.graphql.queries.toLocaleString()} queries</span>
        </Tooltip>
      ),
      sorter: (a, b) => a.graphql.queries - b.graphql.queries,
    },
    {
      title: <Tooltip title="Most recent activity across AI, Dashboards, Graph API"><span><ClockCircleOutlined /> Last Active</span></Tooltip>,
      key: 'last',
      width: 140,
      render: (_, r) => {
        const candidates = [r.ai.last_active, r.dashboards.last_viewed, r.graphql.last_executed].filter(Boolean);
        if (!candidates.length) return <Text type="secondary">—</Text>;
        // Compare via Date so we don't rely on ISO-8601 lexicographic sort
        // staying valid if a future endpoint switches formats.
        const latest = candidates.reduce((a, b) =>
          (new Date(a).getTime() >= new Date(b).getTime() ? a : b)
        );
        return <Tooltip title={fmtDate(latest)}>{fmtRelative(latest)}</Tooltip>;
      },
      sorter: (a, b) => {
        const pickLatest = r => {
          const cs = [r.ai.last_active, r.dashboards.last_viewed, r.graphql.last_executed].filter(Boolean);
          if (!cs.length) return 0;
          return cs.reduce((x, y) => Math.max(x, new Date(y).getTime()), 0);
        };
        return pickLatest(a) - pickLatest(b);
      },
    },
    {
      title: '',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, r) => (
        <Button size="small" type="link" icon={<EyeOutlined />} onClick={() => openUser(r.user_id)}>
          Details
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <TeamOutlined /> User Activity Audit
        </Title>
        <Space>
          <Input.Search
            placeholder="Search user, email…"
            allowClear
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 240 }}
          />
          <Select
            value={days}
            onChange={setDays}
            style={{ width: 150 }}
            options={[
              { value: 7, label: 'Last 7 days' },
              { value: 30, label: 'Last 30 days' },
              { value: 90, label: 'Last 90 days' },
              { value: 365, label: 'Last year' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={loadData} />
        </Space>
      </div>

      <Spin spinning={loading}>
        {summary && (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card><Statistic title="Total Users" value={summary.total_users} prefix={<TeamOutlined />} /></Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card>
                <Statistic
                  title={`Active Users (${days}d)`}
                  value={summary.active_users_window}
                  prefix={<RiseOutlined />}
                  suffix={<Text type="secondary" style={{ fontSize: 12 }}>/ {summary.active_users_7d} in 7d</Text>}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card><Statistic title="Pending Approval" value={summary.pending_users} prefix={<UserOutlined />} valueStyle={summary.pending_users > 0 ? { color: '#d48806' } : {}} /></Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card><Statistic title="AI Messages" value={summary.total_messages} prefix={<MessageOutlined />} /></Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card><Statistic title="AI Tokens" value={summary.total_tokens} prefix={<FireOutlined />} /></Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card><Statistic title="AI Cost (est.)" value={summary.total_cost} prefix={<DollarOutlined />} precision={2} /></Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card><Statistic title="Dashboard Views" value={summary.total_dashboard_views} prefix={<DashboardOutlined />} /></Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card><Statistic title="Graph API Queries" value={summary.total_graphql_queries} prefix={<ApiOutlined />} /></Card>
            </Col>
          </Row>
        )}

        <Card title={`Users (${filteredUsers.length})`} size="small">
          {filteredUsers.length ? (
            <Table
              columns={columns}
              dataSource={filteredUsers}
              rowKey="user_id"
              size="small"
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 1200 }}
            />
          ) : (
            <Empty description="No users match" />
          )}
        </Card>
      </Spin>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={760}
        title={
          detail ? (
            <Space direction="vertical" size={0}>
              <Text strong>{detail.user.full_name || detail.user.username}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{detail.user.email || '—'}</Text>
            </Space>
          ) : 'User Activity'
        }
        destroyOnClose
      >
        <Spin spinning={drawerLoading}>
          {detail && (
            <Tabs
              defaultActiveKey="ai"
              items={[
                {
                  key: 'ai',
                  label: <span><MessageOutlined /> AI Usage</span>,
                  children: (
                    <AIUsageTab models={detail.ai_models} />
                  ),
                },
                {
                  key: 'dash',
                  label: <span><DashboardOutlined /> Dashboards ({detail.dashboards.length})</span>,
                  children: (
                    <DashboardTab rows={detail.dashboards} />
                  ),
                },
                {
                  key: 'gql',
                  label: <span><ApiOutlined /> Graph API ({detail.graphql_tables.length})</span>,
                  children: (
                    <GraphQLTab rows={detail.graphql_tables} />
                  ),
                },
              ]}
            />
          )}
        </Spin>
      </Drawer>
    </div>
  );
}

function AIUsageTab({ models }) {
  if (!models.length) return <Empty description="No AI activity in this window" />;
  const totals = models.reduce(
    (acc, m) => ({
      messages: acc.messages + m.messages,
      tokens: acc.tokens + m.total_tokens,
      cost: acc.cost + m.cost,
    }),
    { messages: 0, tokens: 0, cost: 0 }
  );
  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}><Card size="small"><Statistic title="Messages" value={totals.messages} /></Card></Col>
        <Col span={8}><Card size="small"><Statistic title="Tokens" value={totals.tokens} /></Card></Col>
        <Col span={8}><Card size="small"><Statistic title="Cost (est.)" value={totals.cost} precision={4} prefix="$" /></Card></Col>
      </Row>
      <Table
        size="small"
        rowKey="model_key"
        pagination={false}
        dataSource={models}
        columns={[
          { title: 'Model', dataIndex: 'model_key', render: v => MODEL_LABELS[v] || v },
          { title: 'Messages', dataIndex: 'messages', align: 'right', render: v => v.toLocaleString() },
          { title: 'Input', dataIndex: 'input_tokens', align: 'right', render: v => v.toLocaleString() },
          { title: 'Output', dataIndex: 'output_tokens', align: 'right', render: v => v.toLocaleString() },
          { title: 'Cost', dataIndex: 'cost', align: 'right', render: v => `$${v.toFixed(4)}` },
          { title: 'Last', dataIndex: 'last_active', render: v => <Tooltip title={fmtDate(v)}>{fmtRelative(v)}</Tooltip> },
        ]}
      />
    </>
  );
}

function DashboardTab({ rows }) {
  if (!rows.length) return <Empty description="No dashboard views in this window" />;
  return (
    <Table
      size="small"
      rowKey="dashboard_id"
      pagination={{ pageSize: 15 }}
      dataSource={rows}
      columns={[
        {
          title: 'Dashboard',
          dataIndex: 'name',
          render: (v, r) => (
            <Space>
              <Text>{v}</Text>
              {r.is_owner && <Tag color="blue">owner</Tag>}
            </Space>
          ),
        },
        {
          title: 'Views',
          dataIndex: 'views',
          align: 'right',
          width: 100,
          render: v => v.toLocaleString(),
          sorter: (a, b) => a.views - b.views,
          defaultSortOrder: 'descend',
        },
        {
          title: 'Last Viewed',
          dataIndex: 'last_viewed',
          width: 180,
          render: v => <Tooltip title={fmtDate(v)}>{fmtRelative(v)}</Tooltip>,
        },
      ]}
    />
  );
}

function GraphQLTab({ rows }) {
  if (!rows.length) return <Empty description="No Graph API activity in this window" />;
  return (
    <Table
      size="small"
      rowKey="table"
      pagination={{ pageSize: 15 }}
      dataSource={rows}
      columns={[
        {
          title: <span><TableOutlined /> Table / View</span>,
          dataIndex: 'table',
        },
        {
          title: 'Queries',
          dataIndex: 'queries',
          align: 'right',
          width: 100,
          render: v => v.toLocaleString(),
          sorter: (a, b) => a.queries - b.queries,
          defaultSortOrder: 'descend',
        },
        {
          title: 'Avg Time',
          dataIndex: 'avg_time_ms',
          align: 'right',
          width: 110,
          render: v => `${v?.toLocaleString() || 0} ms`,
        },
        {
          title: 'Errors',
          dataIndex: 'errors',
          align: 'right',
          width: 90,
          render: v => v > 0 ? <Tag color="red">{v}</Tag> : <Text type="secondary">0</Text>,
        },
        {
          title: 'Last',
          dataIndex: 'last_executed',
          width: 160,
          render: v => <Tooltip title={fmtDate(v)}>{fmtRelative(v)}</Tooltip>,
        },
      ]}
    />
  );
}

export default UserActivity;
